import React, { useEffect, useState, useMemo } from "react";
import SafeImage from "../../layout/reusable/SafeImage";
import { getGalleries } from "../../utils/cms.utils";

const PortfolioFilter = () => {
    const [data, setData] = useState([]);
    const [visibleCount, setVisibleCount] = useState(6);
    const [activeTag, setActiveTag] = useState("all");

    const [zoomItem, setZoomItem] = useState(null);
    const [scale, setScale] = useState(1);
    const [flip, setFlip] = useState(1);
    const [rotate, setRotate] = useState(0);

    useEffect(() => {
        const galleries = getGalleries();
        setData(Array.isArray(galleries) ? galleries : []);
    }, []);

    /* =========================
       TAG NORMALIZATION
    ========================== */
    const normalizedData = useMemo(() => {
        return data.map((item) => ({
            ...item,
            tagList: item.tags
                ? item.tags.split(",").map((t) => t.trim())
                : [],
        }));
    }, [data]);

    /* =========================
       AVAILABLE TAGS
    ========================== */
    const tags = useMemo(() => {
        const set = new Set();
        normalizedData.forEach((item) => {
            item.tagList.forEach((tag) => set.add(tag));
        });
        return Array.from(set);
    }, [normalizedData]);

    /* =========================
       FILTERED DATA
    ========================== */
    const filteredData = useMemo(() => {
        if (activeTag === "all") return normalizedData;
        return normalizedData.filter((item) =>
            item.tagList.includes(activeTag)
        );
    }, [normalizedData, activeTag]);

    const visibleData = filteredData.slice(0, visibleCount);

    /* =========================
       ZOOM HANDLERS
    ========================== */
    const openZoom = (item) => {
        setZoomItem(item);
        setScale(1);
        setFlip(1);
        setRotate(0);
        document.body.style.overflow = "hidden";
    };

    const closeZoom = () => {
        setZoomItem(null);
        setScale(1);
        setFlip(1);
        setRotate(0);
        document.body.style.overflow = "";
    };

    return (
        <div className="gallery__area section-padding">

            {/* ===== SECTION TITLE ===== */}
            <div className="gallery__header">
                <p className="gallery__subtitle">Our Moments</p>
                <h2 className="gallery__title">
                    Capturing Work, Culture, and Impact
                </h2>
                <p className="gallery__desc">
                    A visual journey showcasing our projects, workplace activities,
                    and social responsibility initiatives.
                </p>
            </div>

            {/* ===== FILTER ===== */}
            {tags.length > 0 && (
                <div className="gallery__filters">
                    <button
                        className={`gallery__filter ${
                            activeTag === "all" ? "is-active" : ""
                        }`}
                        onClick={() => {
                            setActiveTag("all");
                            setVisibleCount(6);
                        }}
                    >
                        All
                    </button>

                    {tags.map((tag) => (
                        <button
                            key={tag}
                            className={`gallery__filter ${
                                activeTag === tag ? "is-active" : ""
                            }`}
                            onClick={() => {
                                setActiveTag(tag);
                                setVisibleCount(6);
                            }}
                        >
                            {tag}
                        </button>
                    ))}
                </div>
            )}

            <div className="container">
                <div className="row">
                    {visibleData.map((item) => (
                        <div key={item.id} className="col-lg-4 col-md-6 mt-25">
                            <div
                                className="gallery__card"
                                onClick={() => openZoom(item)}
                            >
                                <div className="gallery__frame">
                                    <SafeImage
                                        src={item.image}
                                        alt={item.title || "Gallery Image"}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* ===== SHOW MORE ===== */}
                {visibleCount < filteredData.length && (
                    <div className="gallery__show-more-wrap">
                        <button
                            className="gallery__show-more"
                            onClick={() => setVisibleCount((v) => v + 6)}
                        >
                            Show More
                        </button>
                    </div>
                )}
            </div>

            {/* ===== ZOOM LAYER ===== */}
            {zoomItem && (
                <>
                    <div className="gallery__overlay" onClick={closeZoom} />

                    <div
                        className="gallery__zoom-wrapper"
                        style={{
                            "--scale": scale,
                            "--flip": flip,
                            "--rotate": `${rotate}deg`,
                        }}
                    >
                        <img
                            src={zoomItem.image}
                            alt={zoomItem.title || "Zoom Image"}
                        />
                    </div>

                    <div className="toolbar_wrapper">
                        {zoomItem.title && (
                            <div className="gallery__zoom-text">
                                <p>{zoomItem.title}</p>
                            </div>
                        )}

                        <div
                            className="gallery__toolbar"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button onClick={() => setScale((s) => Math.min(s + 0.2, 3))}>＋</button>
                            <button onClick={() => setScale((s) => Math.max(s - 0.2, 0.5))}>－</button>
                            <button onClick={() => setFlip((f) => f * -1)}>⇋</button>
                            <button onClick={() => setRotate((r) => r + 90)}>⟳</button>
                            <button onClick={() => {
                                setScale(1);
                                setFlip(1);
                                setRotate(0);
                            }}>⟲</button>
                            <button onClick={closeZoom}>✕</button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PortfolioFilter;
